import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Plus,
  GripVertical,
  Trash2,
  Eye,
  ToggleLeft,
  ToggleRight,
  Copy,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { intakeApi } from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";

interface IntakeField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  options: string[];
  order: number;
}

interface IntakeForm {
  _id: string;
  name: string;
  description: string | null;
  fields: IntakeField[];
  active: boolean;
  createdAt: string;
}

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
  { value: "date", label: "Date" },
  { value: "number", label: "Number" },
];

const IntakeFormsPage: React.FC = () => {
  const [forms, setForms] = useState<IntakeForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingForm, setEditingForm] = useState<IntakeForm | null>(null);

  // Form builder state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [fields, setFields] = useState<IntakeField[]>([]);
  const [saving, setSaving] = useState(false);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await intakeApi.listForms();
      setForms(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch forms:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const openCreateForm = () => {
    setEditingForm(null);
    setFormName("");
    setFormDescription("");
    setFields([]);
    setShowFormBuilder(true);
  };

  const openEditForm = (form: IntakeForm) => {
    setEditingForm(form);
    setFormName(form.name);
    setFormDescription(form.description || "");
    setFields(form.fields.map((f, i) => ({ ...f, order: f.order || i })));
    setShowFormBuilder(true);
  };

  const addField = () => {
    const key = `field_${Date.now()}`;
    setFields([
      ...fields,
      {
        key,
        label: "",
        type: "text",
        required: false,
        options: [],
        order: fields.length,
      },
    ]);
  };

  const updateField = (index: number, updates: Partial<IntakeField>) => {
    setFields((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        const updated = { ...f, ...updates };
        // Auto-generate key from label
        if (updates.label !== undefined) {
          updated.key = updates.label
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "");
        }
        return updated;
      })
    );
  };

  const removeField = (index: number) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
  };

  const moveField = (index: number, direction: "up" | "down") => {
    const newFields = [...fields];
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= newFields.length) return;
    [newFields[index], newFields[target]] = [newFields[target], newFields[index]];
    setFields(newFields.map((f, i) => ({ ...f, order: i })));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        name: formName,
        description: formDescription || null,
        fields: fields.map((f, i) => ({ ...f, order: i })),
      };

      if (editingForm) {
        await intakeApi.updateForm(editingForm._id, data);
      } else {
        await intakeApi.createForm(data);
      }
      setShowFormBuilder(false);
      fetchForms();
    } catch (err) {
      console.error("Failed to save form:", err);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (form: IntakeForm) => {
    try {
      if (form.active) {
        await intakeApi.deactivateForm(form._id);
      } else {
        await intakeApi.updateForm(form._id, { active: true });
      }
      fetchForms();
    } catch (err) {
      console.error("Failed to toggle form:", err);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Intake Forms</h1>
          <p className="text-sm text-slate-500 mt-1">
            Build custom forms for client intake and lead capture
          </p>
        </div>
        <button
          onClick={openCreateForm}
          className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <Plus className="h-5 w-5" />
          New Form
        </button>
      </div>

      {/* Forms List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : forms.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20 text-slate-500">
          <FileText className="h-12 w-12 mb-3 text-slate-300" />
          <p className="font-medium text-lg">No intake forms</p>
          <p className="text-sm mt-1">Create your first form to start capturing leads</p>
          <button
            onClick={openCreateForm}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-xl text-sm"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            <Plus className="h-4 w-4" />
            Create First Form
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {forms.map((form, i) => (
            <motion.div
              key={form._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: form.active ? "#e8f5e9" : "#f5f5f5" }}
                  >
                    <FileText
                      className="h-5 w-5"
                      style={{ color: form.active ? "var(--color-primary)" : "#9e9e9e" }}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{form.name}</h3>
                    <div className="flex items-center gap-3 mt-0.5">
                      {form.description && (
                        <p className="text-sm text-slate-500">{form.description}</p>
                      )}
                      <span className="text-xs text-slate-400">
                        {form.fields.length} field{form.fields.length !== 1 ? "s" : ""}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          form.active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {form.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingForm(form);
                      setFormName(form.name);
                      setFormDescription(form.description || "");
                      setFields(form.fields);
                      setShowPreview(true);
                    }}
                    className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
                    title="Preview"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => toggleActive(form)}
                    className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
                    title={form.active ? "Deactivate" : "Activate"}
                  >
                    {form.active ? (
                      <ToggleRight className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <ToggleLeft className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    onClick={() => openEditForm(form)}
                    className="px-4 py-2 text-sm font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Form Builder Modal */}
      <PremiumModal
        open={showFormBuilder}
        onClose={() => setShowFormBuilder(false)}
        title={editingForm ? "Edit Form" : "New Intake Form"}
        subtitle="Build custom fields for client intake"
        icon={FileText}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowFormBuilder(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="intake-form-builder"
              disabled={saving || !formName}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              {saving ? "Saving..." : editingForm ? "Update Form" : "Create Form"}
            </button>
          </div>
        }
      >
        <form id="intake-form-builder" onSubmit={handleSave} className="space-y-6">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Form Name *</label>
              <input
                type="text"
                required
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                placeholder="e.g., New Client Intake"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                rows={2}
                placeholder="Brief description of this form"
              />
            </div>
          </div>

          {/* Fields */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Fields</h3>
              <button
                type="button"
                onClick={addField}
                className="text-sm font-medium flex items-center gap-1"
                style={{ color: "var(--color-primary)" }}
              >
                <Plus className="h-4 w-4" />
                Add Field
              </button>
            </div>

            {fields.length === 0 ? (
              <div className="bg-slate-50 rounded-xl p-6 text-center text-sm text-slate-500">
                No fields yet. Click "Add Field" to start building your form.
              </div>
            ) : (
              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div
                    key={index}
                    className="bg-slate-50 rounded-xl p-4 border border-slate-200"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col gap-0.5 mt-1">
                        <button
                          type="button"
                          onClick={() => moveField(index, "up")}
                          disabled={index === 0}
                          className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30"
                        >
                          <ChevronUp className="h-3 w-3 text-slate-500" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveField(index, "down")}
                          disabled={index === fields.length - 1}
                          className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30"
                        >
                          <ChevronDown className="h-3 w-3 text-slate-500" />
                        </button>
                      </div>

                      <div className="flex-1 grid grid-cols-3 gap-3">
                        <div className="col-span-1">
                          <input
                            type="text"
                            placeholder="Field label"
                            value={field.label}
                            onChange={(e) => updateField(index, { label: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                          />
                        </div>
                        <div>
                          <SearchableSelect
                            options={FIELD_TYPES}
                            value={field.type}
                            onChange={(val) => updateField(index, { type: val || "text" })}
                            placeholder="Field type"
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-1.5 text-sm text-slate-600">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(e) => updateField(index, { required: e.target.checked })}
                              className="h-3.5 w-3.5 rounded border-slate-300"
                            />
                            Required
                          </label>
                          <button
                            type="button"
                            onClick={() => removeField(index)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors ml-auto"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Options for select type */}
                    {field.type === "select" && (
                      <div className="mt-3 ml-8">
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          Options (comma separated)
                        </label>
                        <input
                          type="text"
                          placeholder="Option 1, Option 2, Option 3"
                          value={field.options.join(", ")}
                          onChange={(e) =>
                            updateField(index, {
                              options: e.target.value.split(",").map((o) => o.trim()),
                            })
                          }
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>
      </PremiumModal>

      {/* Preview Modal */}
      <PremiumModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        title={`Preview: ${formName}`}
        subtitle={formDescription || undefined}
        icon={Eye}
        size="sm"
        footer={
          <button
            disabled
            className="w-full px-5 py-3 rounded-xl text-white text-sm font-bold opacity-60"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            Submit
          </button>
        }
      >
        <div className="space-y-4">
          {fields.map((field, i) => (
            <div key={i}>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {field.label || `Field ${i + 1}`}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.type === "textarea" ? (
                <textarea
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm"
                  rows={3}
                  disabled
                />
              ) : field.type === "select" ? (
                <SearchableSelect
                  options={field.options.map((opt) => ({ value: opt, label: opt }))}
                  value=""
                  onChange={() => {}}
                  placeholder="Select..."
                  disabled
                />
              ) : field.type === "checkbox" ? (
                <div className="flex items-center gap-2">
                  <input type="checkbox" disabled className="h-4 w-4 rounded" />
                  <span className="text-sm text-slate-500">Yes</span>
                </div>
              ) : (
                <input
                  type={field.type}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm"
                  disabled
                />
              )}
            </div>
          ))}
        </div>
      </PremiumModal>
    </div>
  );
};

export default IntakeFormsPage;
